import React, { useEffect, useState } from "react";
import { Header } from "build-ts/metadata";
import SingleListComponent from "components/common/SingleListComponent";
import MultiListComponent from "components/common/MultiListComponent";

interface PlanHeaderListProps {
  planHeaders: Header[];
  setPlanHeaders?: (headers: Header[]) => void;
  selectedPlanHeaders: Header[];
  setSelectedPlanHeaders?: (headers: Header[]) => void;
  isMultiSelect?: boolean;
  // Single select props
  onPlanSelection?: (header: Header) => Promise<void>;
  onCreateNewPlan?: (planName: string) => Promise<void>;
  // Multi select props  
  onMultiPlanSelection?: (headers: Header[]) => void;
  isSidebarVisible?: boolean;
  toggleSidebar?: () => void;
}

const ListPlans: React.FC<PlanHeaderListProps> = ({
  planHeaders,
  setPlanHeaders,
  selectedPlanHeaders,
  setSelectedPlanHeaders,
  isMultiSelect = false,
  onPlanSelection,
  onCreateNewPlan,
  onMultiPlanSelection,
  isSidebarVisible,
  toggleSidebar,
}) => {
  // Search and filtering logic
  const [filteredHeaders, setFilteredHeaders] = useState<Header[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter headers based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredHeaders(planHeaders);
    } else {
      const filtered = planHeaders?.filter((header) =>
        header?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHeaders(filtered);
    }
  }, [planHeaders, searchTerm]);

  // Handlers for single select mode
  const handlePlanSelection = (header: Header) => {
    if (onPlanSelection) {
      onPlanSelection(header);
    }
  };

  // Handlers for multi select mode
  const handleMultiPlanToggle = (header: Header) => {
    const isSelected = selectedPlanHeaders.some(h => h.id === header.id);
    let newSelectedHeaders: Header[];
    
    if (isSelected) {
      // Remove from selection
      newSelectedHeaders = selectedPlanHeaders.filter(h => h.id !== header.id);
    } else {
      // Add to selection
      newSelectedHeaders = [...selectedPlanHeaders, header];
    }
    
    setSelectedPlanHeaders?.(newSelectedHeaders);
  };

  const isSelected = (header: Header) => {
    return selectedPlanHeaders.some(h => h.id === header.id);
  };

  if (isMultiSelect) {
    return (
      <MultiListComponent
        filteredHeaders={filteredHeaders}
        selectedPlanHeaders={selectedPlanHeaders}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onPlanToggle={handleMultiPlanToggle}
        isSelected={isSelected}
      />
    );
  }

  return (
    <SingleListComponent
      filteredHeaders={filteredHeaders}
      selectedPlanHeaders={selectedPlanHeaders}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onPlanSelection={handlePlanSelection}
      onCreateNewPlan={onCreateNewPlan}
      setPlanHeaders={setPlanHeaders}
      isSidebarVisible={isSidebarVisible}
      toggleSidebar={toggleSidebar}
    />
  );
};

export default ListPlans;
